import type { Action } from '../shared/orderReducer';
import type { MenuAction } from '../shared/menuReducer';
import type { LayoutAction } from '../shared/layoutReducer';
import type { OrderState, MenuState, LayoutState, TenantRoster, TenantSummary } from '../shared/types';
import type { StatsSummary } from '../shared/statsAggregate';
import { getToken, clearToken } from './authClient';

const API_URL = process.env.REACT_APP_API_URL ?? '';

export interface RemoteState extends OrderState {
  version: number;
  updatedAt: number;
}

export interface RemoteMenuState extends MenuState {
  version: number;
  updatedAt: number;
}

export class UnauthorizedError extends Error {}

function authHeaders(): Record<string, string> {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function checkAuthResponse(res: Response): void {
  if (res.status === 401) {
    clearToken();
    throw new UnauthorizedError('Not authenticated');
  }
}

export async function fetchState(): Promise<RemoteState> {
  const res = await fetch(`${API_URL}/state`, { headers: authHeaders() });
  checkAuthResponse(res);
  if (!res.ok) throw new Error(`GET /state failed: ${res.status}`);
  return res.json();
}

export async function postAction(action: Action): Promise<RemoteState> {
  const res = await fetch(`${API_URL}/actions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(action),
  });
  checkAuthResponse(res);
  if (!res.ok) throw new Error(`POST /actions failed: ${res.status}`);
  return res.json();
}

export async function fetchMenu(): Promise<RemoteMenuState> {
  const res = await fetch(`${API_URL}/menu`, { headers: authHeaders() });
  checkAuthResponse(res);
  if (!res.ok) throw new Error(`GET /menu failed: ${res.status}`);
  return res.json();
}

export async function postMenuAction(action: MenuAction): Promise<RemoteMenuState> {
  const res = await fetch(`${API_URL}/menu/actions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(action),
  });
  checkAuthResponse(res);
  if (!res.ok) throw new Error(`POST /menu/actions failed: ${res.status}`);
  return res.json();
}

export interface RemoteLayoutState extends LayoutState {
  version: number;
  updatedAt: number;
}

export async function fetchLayout(): Promise<RemoteLayoutState> {
  const res = await fetch(`${API_URL}/layout`, { headers: authHeaders() });
  checkAuthResponse(res);
  if (!res.ok) throw new Error(`GET /layout failed: ${res.status}`);
  return res.json();
}

export async function postLayoutAction(action: LayoutAction): Promise<RemoteLayoutState> {
  const res = await fetch(`${API_URL}/layout/actions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(action),
  });
  checkAuthResponse(res);
  if (!res.ok) throw new Error(`POST /layout/actions failed: ${res.status}`);
  return res.json();
}

export async function fetchStats(from: number, to: number): Promise<StatsSummary> {
  const res = await fetch(`${API_URL}/stats?from=${from}&to=${to}`, { headers: authHeaders() });
  checkAuthResponse(res);
  if (!res.ok) throw new Error(`GET /stats failed: ${res.status}`);
  return res.json();
}

export type RosterAction =
  | { type: 'ADD_STAFF'; email: string }
  | { type: 'REMOVE_STAFF'; email: string }
  | { type: 'SET_ADMIN'; email: string; isAdmin: boolean };

export async function fetchRoster(): Promise<TenantRoster> {
  const res = await fetch(`${API_URL}/tenant/roster`, { headers: authHeaders() });
  checkAuthResponse(res);
  if (!res.ok) throw new Error(`GET /tenant/roster failed: ${res.status}`);
  return res.json();
}

export async function postRosterAction(action: RosterAction): Promise<TenantRoster> {
  const res = await fetch(`${API_URL}/tenant/roster/actions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(action),
  });
  checkAuthResponse(res);
  if (!res.ok) {
    const body: { message?: string } = await res.json().catch(() => ({}));
    throw new Error(body.message ?? `POST /tenant/roster/actions failed: ${res.status}`);
  }
  return res.json();
}

export type TenantAction =
  | { type: 'CREATE_TENANT'; name: string; code: string; adminEmail: string }
  | { type: 'RENAME_TENANT'; tenantId: string; name: string }
  | { type: 'DEACTIVATE_TENANT'; tenantId: string }
  | { type: 'REACTIVATE_TENANT'; tenantId: string };

export async function fetchTenants(): Promise<TenantSummary[]> {
  const res = await fetch(`${API_URL}/tenants`, { headers: authHeaders() });
  checkAuthResponse(res);
  if (!res.ok) throw new Error(`GET /tenants failed: ${res.status}`);
  return res.json();
}

export async function postTenantAction(action: TenantAction): Promise<TenantSummary> {
  const res = await fetch(`${API_URL}/tenants/actions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(action),
  });
  checkAuthResponse(res);
  if (!res.ok) {
    const body: { message?: string } = await res.json().catch(() => ({}));
    throw new Error(body.message ?? `POST /tenants/actions failed: ${res.status}`);
  }
  return res.json();
}
